/**
 * RevenueCalculator
 *
 * Interactive revenue calculator for the analytics dashboard.
 * Allows users to input different plan user counts and see revenue projections.
 */
import React from 'react';

const RevenueCalculator = ({ 
  users22, 
  setUsers22, 
  users34, 
  setUsers34, 
  annualUsers, 
  setAnnualUsers,
  analyticsData,
  formatCurrency 
}) => {
  const revenue22 = users22 * 22;
  const revenue34 = users34 * 34;
  const revenueAnnual = annualUsers * 24; // Annual users contribute $24/month equivalent
  const totalRevenue = revenue22 + revenue34 + revenueAnnual;
  const totalExpenses = (150 * 30) + 650;
  const profit = totalRevenue - totalExpenses;

  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue Calculator</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Users at $22/month (legacy pricing)
            </label>
            <input
              type="number"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              value={users22 || ''}
              onChange={(e) => setUsers22(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Users at $34/month (current pricing)
            </label>
            <input
              type="number"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              value={users34 || ''}
              onChange={(e) => setUsers34(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Annual Users at $24/month equivalent
            </label>
            <input
              type="number"
              min="0"
              className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              value={annualUsers || ''}
              onChange={(e) => setAnnualUsers(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Annual subscribers (calculated as monthly equivalent revenue)
            </p>
          </div>
          
          {/* Total Users Summary */}
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Users in Calculator:</span>
              <span className="font-semibold text-gray-900 dark:text-white">{users22 + users34 + annualUsers}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Current Paying Users:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {analyticsData.summary?.payingCustomers || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">MRR from $22 users:</span>
              <span className="font-semibold text-gray-900 dark:text-white">${revenue22.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">MRR from $34 users:</span>
              <span className="font-semibold text-gray-900 dark:text-white">${revenue34.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">MRR from annual users:</span>
              <span className="font-semibold text-gray-900 dark:text-white">${revenueAnnual.toLocaleString()}</span>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900 dark:text-white">Total MRR:</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">${totalRevenue.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Monthly Expenses (ads + fixed):</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency((150 * 30) + 650)}</span>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900 dark:text-white">
                  {profit >= 0 ? 'Profit:' : 'Loss:'}
                </span>
                <span className={`text-xl font-bold ${
                  profit >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {profit >= 0 ? '+' : ''}${profit.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {profit < 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                To break even, you need ${Math.abs(profit).toLocaleString()} more revenue:
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                • {Math.ceil(Math.abs(profit) / 34)} more $34 users
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueCalculator;


